import {PagedData} from "@/services/models/shared/PagedData";
import {AxiosResponse} from "axios";
import {
    AzureBillingAdjustment,
    AzureBillingCharge,
    AzureBillingFilterOptions,
    AzureBillingListItem,
    AzureBillingSubscription,
    NewAzureBillingAdjustment,
    UpdateSubscription
} from "@/services/models/AzureBilling";
import FileProvider from "@/utilities/providers/FileProvider";
import BimsHttpService from "@/services/BimsHttpService";

class AzureBillingService {
    /**
     * @description Returns a paged list of azure billing id.
     */
    getBillingList(filter: AzureBillingFilterOptions): Promise<PagedData<AzureBillingListItem>> {
        return BimsHttpService.get(`/azureBilling`, {params: filter}).then((response: AxiosResponse<PagedData<AzureBillingListItem>>) => {
            return response.data as PagedData<AzureBillingListItem>;
        });
    }

    /**
     * @description Downloads the azure billing invoice pdf for the given billing id
     */
    public downloadInvoice(billingId: string): Promise<any> {
        return BimsHttpService.get(
            `/azureBilling/${billingId}/export`, {
                responseType: 'blob'
            }).then(response => {
            return FileProvider.ForceFileDownload(response)
        })
    }

    /**
     * @description Returns all subscriptions for the given billing id
     */
    getBillingSubscriptions(billingId: string): Promise<AzureBillingSubscription[]> {
        return BimsHttpService.get(`/azureBilling/${billingId}/subscriptions`).then((response: AxiosResponse<AzureBillingSubscription[]>) => {
            return response.data as AzureBillingSubscription[];
        });
    }

    /**
     * @description Returns all subscriptions for the given billing id
     */
    getSubscription(billingId: string, subscriptionId: string): Promise<AzureBillingSubscription> {
        return BimsHttpService.get(`/azureBilling/${billingId}/subscriptions/${subscriptionId}`).then((response: AxiosResponse<AzureBillingSubscription>) => {
            return response.data as AzureBillingSubscription;
        });
    }

    /**
     * @description Returns all subscriptions for the given billing id
     */
    updateSubscription(billingId: string, subscriptionId: string, subscription: UpdateSubscription): Promise<AzureBillingSubscription> {
        return BimsHttpService.put(`/azureBilling/${billingId}/subscriptions/${subscriptionId}`, subscription).then((response: AxiosResponse<AzureBillingSubscription>) => {
            return response.data as AzureBillingSubscription;
        });
    }

    /**
     * @description Returns all subscriptions for the given billing id
     */
    getSubscriptionCharges(billingId: string, subscriptionId: string): Promise<AzureBillingCharge[]> {
        return BimsHttpService.get(`/azureBilling/${billingId}/subscriptions/${subscriptionId}/charges`).then((response: AxiosResponse<AzureBillingCharge[]>) => {
            return response.data as AzureBillingCharge[];
        });
    }

    /**
     * @description Returns all subscriptions for the given billing id
     */
    getSubscriptionAdjustments(billingId: string, subscriptionId: string): Promise<AzureBillingAdjustment[]> {
        return BimsHttpService.get(`/azureBilling/${billingId}/subscriptions/${subscriptionId}/adjustments`).then((response: AxiosResponse<AzureBillingAdjustment[]>) => {
            return response.data as AzureBillingAdjustment[];
        });
    }

    /**
     * @description Returns all subscriptions for the given billing id
     */
    addSubscriptionAdjustment(billingId: string, subscriptionId: string, adjustment: NewAzureBillingAdjustment): Promise<any> {
        return BimsHttpService.post(`/azureBilling/${billingId}/subscriptions/${subscriptionId}/adjustments`, adjustment).then((response: AxiosResponse<any>) => {
            return response.data;
        });
    }

    /**
     * @description Returns all subscriptions for the given billing id
     */
    deleteSubscriptionAdjustment(id: string): Promise<void> {
        return BimsHttpService.delete(`/azureBilling/subscriptions/adjustments/${id}`);
    }
}

export default new AzureBillingService()
